<template>
  <el-dialog
    title="地图选点"
    :visible.sync="dialogVisible"
    width="80%"
    :before-close="handleClose"
    class="map-dialog"
    append-to-body
    top="5vh"
    :close-on-click-modal="false"
    @closed="handleDialogClosed"
    @opened="handleDialogOpened">
    <div class="map-container" style="height: 60vh; position: relative;">
      <base-map
        ref="baseMap"
        :initial-zoom="13"
        :initial-center="[coordinates.latitude, coordinates.longitude]"
        @map-ready="onMapReady"
        @mouse-move="onMouseMove">
        <template v-slot:map-overlays>
          <l-marker v-if="marker" :lat-lng="marker"></l-marker>
        </template>
      </base-map>
    </div>
    <div class="coordinate-display">
      <span>经度: {{ formatLongitude(coordinates.longitude) }}</span>
      <span class="ml-4">纬度: {{ formatLatitude(coordinates.latitude) }}</span>
    </div>
    <div slot="footer" class="dialog-footer">
      <el-button @click="handleCancel">取消</el-button>
      <el-button type="primary" @click="handleConfirm">确认</el-button>
    </div>
  </el-dialog>
</template>

<script>
import 'leaflet/dist/leaflet.css'
import BaseMap from '@/components/BaseMap.vue'
import { LMarker } from 'vue2-leaflet'
import { formatLatitude, formatLongitude } from '@/utils/coordinate'

export default {
  name: 'MapPicker',
  components: {
    BaseMap,
    LMarker
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    initialCoordinates: {
      type: Object,
      default: () => ({
        longitude: 121.473701,
        latitude: 31.230416
      })
    }
  },
  data() {
    return {
      dialogVisible: this.visible,
      coordinates: {
        longitude: this.initialCoordinates.longitude || 121.473701,
        latitude: this.initialCoordinates.latitude || 31.230416
      },
      map: null,
      marker: null
    }
  },
  watch: {
    visible(newVal) {
      this.dialogVisible = newVal;
    },
    dialogVisible(newVal) {
      this.$emit('update:visible', newVal);
    },
    initialCoordinates: {
      handler(newVal) {
        this.coordinates = {
          longitude: newVal.longitude || 121.473701,
          latitude: newVal.latitude || 31.230416
        };
        
        if (this.coordinates.longitude && this.coordinates.latitude) {
          this.marker = [this.coordinates.latitude, this.coordinates.longitude];
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    onMapReady(map) {
      this.map = map;
      
      // 添加点击事件监听
      if (map) {
        map.on('click', this.handleMapClick);
        
        // 延时执行地图刷新，确保容器尺寸已经稳定
        setTimeout(() => {
          map.invalidateSize();
        }, 300);
      }
    },
    onMouseMove(event) {
      if (!event || !event.latlng) return;
      
      const { lat, lng } = event.latlng;
      this.coordinates = {
        longitude: lng,
        latitude: lat
      };
    },
    handleMapClick(event) {
      // 处理地图点击事件
      if (!event || !event.latlng) return;
      
      const { lat, lng } = event.latlng;
      this.coordinates = {
        longitude: lng,
        latitude: lat
      };
      this.marker = [lat, lng];
    },
    handleClose() {
      this.dialogVisible = false;
    },
    handleCancel() {
      this.dialogVisible = false;
    },
    handleConfirm() {
      this.$emit('confirm', { ...this.coordinates });
      this.dialogVisible = false;
    },
    handleDialogClosed() {
      // 处理地图对话框关闭后的逻辑
      this.$emit('closed');
    },
    handleDialogOpened() {
      // 处理地图对话框打开后的逻辑
      this.$nextTick(() => {
        if (this.$refs.baseMap && this.$refs.baseMap.mapInstance) {
          const map = this.$refs.baseMap.mapInstance;
          // 刷新地图大小
          map.invalidateSize();
          
          // 如果有标记点，则将视图中心设置到标记点
          if (this.marker) {
            map.setView(this.marker, 13);
          }
        }
      });
      
      this.$emit('opened');
    },
    // 格式化经度显示
    formatLongitude(longitude) {
      return formatLongitude(longitude);
    },
    // 格式化纬度显示
    formatLatitude(latitude) {
      return formatLatitude(latitude);
    }
  }
}
</script>

<style scoped>
/* 地图选点对话框样式 */
.map-container {
  position: relative;
  width: 100%;
  height: 60vh;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
}

.coordinate-display {
  margin-top: 16px;
  padding: 12px 16px;
  background-color: #f5f7fa;
  border-radius: 4px;
  font-family: "Courier New", monospace;
  font-size: 14px;
  border: 1px solid #e6e6e6;
}

.ml-4 {
  margin-left: 1rem;
}

/* 地图对话框样式优化 */
.map-dialog {
  display: flex;
  flex-direction: column;
}

/* 覆盖Element UI默认样式 */
.map-dialog >>> .el-dialog {
  margin-top: 5vh !important;
  margin-bottom: 5vh;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

.map-dialog >>> .el-dialog__body {
  padding: 15px 20px;
  flex: 1;
  overflow: hidden;
  max-height: calc(90vh - 108px); /* 减去header和footer的高度 */
}
</style> 